import Banner from '@ben/orion/banner';
import Checkbox from '@ben/orion/checkbox';
import { ButtonVariant } from '@ben/orion/deprecated-button';
import Icon, { IconName } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input from '@ben/orion/input';
import { Modal, ModalVariant } from '@ben/orion/modals';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModalRef } from '@ben/shared/components/modal/modal';
import { CustomComponentTypes } from '@ben/shared/constants';
import { useMemo, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import ComponentKey from '../component-key/component-key';
import RestrictionSet from '../restriction-set/restriction-set';
import { AirtableImportFeedback } from '../shared/airtable-import-feedback';
import ComponentWindow from './component-window';
import modularStepChildComponent from './modular-step-child-component';

interface IModularStepComponentWrapperProps {
  componentControllerName: string;
  type: CustomComponentTypes;
  index: number;
  isExpanded: boolean;
  isImportedFromAirtable?: boolean;
  onToggle: (index: number) => void;
  onRemove: (id: string) => void;
  componentId: string;
}

const ModularStepComponent = ({
  componentControllerName,
  type,
  index,
  isExpanded,
  isImportedFromAirtable,
  onToggle,
  onRemove,
  componentId,
}: IModularStepComponentWrapperProps) => {
  const { control, getFieldState, setValue } = useFormContext();

  const componentName =
    (useWatch({
      name: `${componentControllerName}.name`,
      exact: true,
    }) as string | undefined) || 'No name';
  const { isDirty } = getFieldState(componentControllerName);

  const removeComponentModalRef = useRef<IModalRef>(null);

  const childComponents = useMemo(
    () => modularStepChildComponent(type, componentControllerName),
    [type, componentControllerName]
  );

  const handleRemoveComponent = () => {
    removeComponentModalRef.current?.showModal();
  };

  const handleRemoveStepConfirmation = () => {
    onRemove(componentId);
    toast.info(
      'Component removed successfully, but please save the benefit to reflect the changes.'
    );
  };

  return (
    <Wrapper>
      <ComponentHeaderWrapper
        onClick={() => onToggle(index)}
        aria-expanded={isExpanded}
      >
        <ComponentHeader>
          <ComponentHeaderIconTextWrapper>
            <Chevron $isOpen={isExpanded} name={IconName.ArrowsDown} />
            <Typography variant={TypographyVariant.h3}>{type}</Typography>
          </ComponentHeaderIconTextWrapper>
          <InlineTextButton onClick={handleRemoveComponent} label="Remove" />
        </ComponentHeader>
        <AirtableImportFeedback
          visible={!!isImportedFromAirtable && !isDirty}
        />
      </ComponentHeaderWrapper>
      {type === CustomComponentTypes.PENSION_SLIDERS && (
        <Banner hideIcon>
          <StyledTypography variant={TypographyVariant.bodyMd1}>
            This component is used only for simple pension contributions. To
            create a more complex scenario, please consider using the{' '}
            <span>‘SLIDER’</span> component linked to a{' '}
            <span>‘CALCULATED_VALUE’</span>.
          </StyledTypography>
        </Banner>
      )}
      <ElementsWrapper>
        <Controller
          name={`${componentControllerName}.rank`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={index}
              name="Rank"
              disabled={true}
              width="82px"
            />
          )}
        />
        <Controller
          name={`${componentControllerName}.name`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Component name"
              width="330px"
              errorMessage={fieldState.error?.message}
              hasError={!!fieldState.error}
              onChange={(event) => {
                field.onChange(event);
                setValue(
                  `${componentControllerName}.nameEn`,
                  event.target.value
                );
              }}
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
        />
        <Controller
          name={`${componentControllerName}.showName`}
          control={control}
          render={({ field }) => (
            <StyledCheckbox
              label="Show name"
              value={!!field.value}
              onChange={field.onChange}
            />
          )}
        />
      </ElementsWrapper>
      <VisibilityContainer $isOpen={isExpanded} aria-hidden={!isExpanded}>
        {childComponents}
        {type !== CustomComponentTypes.TEXT_BLOCK && (
          <ElementsWrapper>
            <ComponentKey componentControllerName={componentControllerName} />
            <Controller
              name={`${componentControllerName}.required`}
              control={control}
              render={({ field }) => (
                <StyledCheckbox {...field} label="Is required" />
              )}
            />
            <Controller
              name={`${componentControllerName}.hideForEmployee`}
              control={control}
              render={({ field }) => (
                <StyledCheckbox
                  label="Hide from EA"
                  value={!!field.value}
                  onChange={field.onChange}
                  hintText="When on, this component is hidden from employees in the Employee App but stays visible here and in reports."
                />
              )}
            />
            <Controller
              name={`${componentControllerName}.hideForEcc`}
              control={control}
              render={({ field }) => (
                <StyledCheckbox
                  label="Hide from ECC"
                  value={!!field.value}
                  onChange={field.onChange}
                  hintText="When on, this component is hidden from the ECC but stays available to reports and document templates."
                />
              )}
            />
          </ElementsWrapper>
        )}
        {type !== CustomComponentTypes.TEXT_BLOCK &&
          type !== CustomComponentTypes.CALCULATED_VALUE && (
            <>
              <ComponentWindow
                componentControllerName={`${componentControllerName}.windows`}
              />
              <RestrictionSet
                componentType={type}
                componentControllerName={componentControllerName}
              />
            </>
          )}
      </VisibilityContainer>
      <Modal
        ref={removeComponentModalRef}
        title="Remove component"
        description={`<p>Are you sure you want to remove the component <strong>‘${type}: ${componentName}’</strong> ? </p>`}
        primaryText="Remove"
        secondaryText="Cancel"
        primaryVariant={ButtonVariant.error}
        onConfirm={handleRemoveStepConfirmation}
        icon={IconName.Trash}
        variant={ModalVariant.Icon}
      />
    </Wrapper>
  );
};

export default ModularStepComponent;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    padding: ${theme.spacing(3)};
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(3)};
    align-self: stretch;
    border-radius: 10px;
    background: ${theme.palette.neutral[40]};
    border: 1px solid ${theme.palette.primary.main};
  `};
`;

const ComponentHeaderWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0)};
  `};
`;

const ComponentHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ComponentHeaderIconTextWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(1)};
  `};
`;

const Chevron = styled(Icon)<{ $isOpen: boolean }>`
  transform: rotate(${({ $isOpen }) => ($isOpen ? '0deg' : '-90deg')});
  cursor: pointer;
`;

const ElementsWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${theme.spacing(2)};
  `}
`;

const StyledCheckbox = styled(Checkbox)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(4)};
  `}
`;

const StyledTypography = styled(Typography)`
  ${({ theme }) => css`
    span {
      font-weight: ${theme.typography.bodyMd2};
    }
  `}
`;

const VisibilityContainer = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'contents' : 'none')};
`;
